import React, { useState, useEffect } from 'react';
import { RoomData, ScheduleRitual } from '../types';
import { db, doc, setDoc, safeOnSnapshot, collection, deleteDoc, getDoc } from '../firebaseConfig';
import { isStudent } from '../utils/roles';
import { logSisterAction } from '../sisterAudit';
interface TheForgeProps {
  currentUser?: any;
  canDelete?: boolean;
  onClose: () => void;
  rooms: Record<string, RoomData>;
  initialRoomId?: string;
}

const DAYS = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'] as const;
const TYPES = ['Lektion', 'Ritual', 'Samling', 'Event'] as const;
const REPEAT_OPTIONS = [
  { id: 'none', label: 'Ingen' },
  { id: 'weekly', label: 'Hver uge' },
  { id: 'daily', label: 'Hver dag' }
] as const;

// NYT: Adgangsniveauer til skemaet
const ACCESS_LEVELS = [
  { id: 'all', label: 'Alle' },
  { id: 'basis', label: '🌱 Basis+' },
  { id: 'standard', label: '📦 Standard+' },
  { id: 'plus', label: '💎 Plus' }
] as const;

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') {
    return val.da || val.en || val.nb || Object.values(val).find(v => typeof v === 'string') || "";
  }
  return String(val);
};

const TheForge: React.FC<TheForgeProps> = ({ currentUser, onClose, rooms, initialRoomId, canDelete = false }) => {
  const fgToday = new Date().toLocaleDateString('sv-SE');
  const [showArchive, setShowArchive] = useState(false);
  const fgIsEnded = (r: any) => !!(r.seriesEnd && r.seriesEnd < fgToday);
  const fgDate = (d: string) => d.slice(8, 10) + '.' + d.slice(5, 7) + '.' + d.slice(2, 4);
  const [selectedRoomId, setSelectedRoomId] = useState<string>(initialRoomId || '');
  const [targetRoomIds, setTargetRoomIds] = useState<string[]>(initialRoomId ? [initialRoomId] : []);
  const [isGlobalSettings, setIsGlobalSettings] = useState<boolean>(!initialRoomId);
  const [rituals, setRituals] = useState<ScheduleRitual[]>([]);
  
  const [academyUrl, setAcademyUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [linkedSubjects, setLinkedSubjects] = useState([]);
  const [hallsData, setHallsData] = useState([]);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [day, setDay] = useState<typeof DAYS[number]>('Mandag');
  const [time, setTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [repeat, setRepeat] = useState<ScheduleRitual['repeat']>('none');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [seriesEnd, setSeriesEnd] = useState('');
  const [visibleInVacations, setVisibleInVacations] = useState(false);
  const [editingOriginal, setEditingOriginal] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState<typeof TYPES[number]>('Lektion');
  const [isVisible, setIsVisible] = useState(true);
  const [hiddenUntil, setHiddenUntil] = useState<string>('');
  
  // NYT: State til adgangsniveau
  const [accessLevel, setAccessLevel] = useState<'all' | 'basis' | 'standard' | 'plus'>('all');
  const [targetRoomId, setTargetRoomId] = useState<string>('');
  const [vacationFrom, setVacationFrom] = useState<string>('');
  const [guardians, setGuardians] = useState<any[]>([]);
  const [assignedGuardianId, setAssignedGuardianId] = useState<string>('');
  const [assignedGuardianName, setAssignedGuardianName] = useState<string>('');
  const [sendNotification, setSendNotification] = useState(false);
  const [copyToRoomId, setCopyToRoomId] = useState<string>('');
  const [allowedAgeGroups, setAllowedAgeGroups] = useState<string[]>([]);
  const [allowedStudentIds, setAllowedStudentIds] = useState<string[]>([]);
  const [classroomStudents, setClassroomStudents] = useState<any[]>([]);
  const [vacationUntil, setVacationUntil] = useState<string>('');

  useEffect(() => {
    const fetchGuardians = async () => {
      const { getDocs, query: q, collection: col, where: wh } = await import("firebase/firestore");
      const snap = await getDocs(q(col(db, "users"), wh("role", "in", ["guardian", "admin", "superadmin"])));
      setGuardians(snap.docs.map(d => ({...d.data(), id: d.id})));
    };
    fetchGuardians();
    const fetchGlobal = async () => {
      const snap = await getDoc(doc(db, "rooms", "config_global"));
      if (snap.exists()) setAcademyUrl(snap.data().mainAcademyUrl || "");
    };
    fetchGlobal();
    const fetchHalls = async () => {
      // Phase 3: hent classrooms + flat subjects collection i parallel
      const { getDocs, collection: col } = await import("firebase/firestore");
      const [classSnap, subjSnap] = await Promise.all([
        getDocs(col(db, "classrooms")),
        getDocs(col(db, "subjects")),
      ]);
      const allSubj = subjSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      setHallsData(classSnap.docs.map(d => {
        const cData = d.data() as any;
        const flatForClass = allSubj.filter((s: any) => s.classroomId === d.id);
        const subjects = flatForClass.length > 0 ? flatForClass : (cData.subjects || []);
        return { id: d.id, name: cData.name || '', subjects };
      }));
    };
    fetchHalls();
    // Hent elever fra alle classrooms til studentfilter
    const fetchStudents = async () => {
      const { getDocs, collection: col } = await import("firebase/firestore");
      const snap = await getDocs(col(db, "users"));
      setClassroomStudents(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((u: any) => isStudent(u.role)));
    };
    fetchStudents();
  }, []);

  useEffect(() => {
    if (!selectedRoomId || isGlobalSettings) return;
    const unsub = safeOnSnapshot(collection(db, `room_schedules/${selectedRoomId}/rituals`), (snap: any) => {
      const r: ScheduleRitual[] = [];
      snap.forEach((d: any) => r.push({ ...d.data(), id: d.id } as ScheduleRitual));
      setRituals(r.sort((a, b) => a.time.localeCompare(b.time)));
    });
    return () => unsub();
  }, [selectedRoomId, isGlobalSettings]);

  const handleSaveRitual = async () => {
    if (targetRoomIds.length === 0 || !title || !time) return;
    setIsSaving(true);

    const commonGroupId = editingGroupId || Date.now().toString();
    const hiddenUntilTimestamp = hiddenUntil ? new Date(hiddenUntil).getTime() : undefined;

    try {
      // dupfix: redigering opdaterer ALTID eksisterende id \u2014 aldrig nye kopier
      const effRooms = editingId ? [targetRoomIds[0]] : targetRoomIds;
      for (const roomId of effRooms) {
        const id = editingId ? editingId : `${commonGroupId}_${roomId}`;
        const ritual: ScheduleRitual = { 
          ...(editingId && editingOriginal ? (editingOriginal as any) : {}),
          id, 
          day, 
          time, 
          endTime, 
          repeat, 
          title, 
          description: desc, 
          type,
          isVisible,
          ...(hiddenUntilTimestamp !== undefined ? { hiddenUntil: hiddenUntilTimestamp } : {}),
          repeatInterval: repeatInterval || 1,
          ...(seriesEnd ? { seriesEnd } : {}),
          visibleInVacations,
          ...(editingId ? {} : { seriesStart: new Date().toLocaleDateString('sv-SE') }),
          groupId: commonGroupId,
          sourceRoomId: roomId,
          accessLevel,
          ...(assignedGuardianId ? { assignedGuardianId } : {}),
          ...(assignedGuardianName ? { assignedGuardianName } : {}),
          ...(allowedAgeGroups.length > 0 ? { allowedAgeGroups } : {}),
          ...(targetRoomId ? { targetRoomId } : {}),
          vacationFrom: vacationFrom ? new Date(vacationFrom).getTime() : 0,
          vacationUntil: vacationUntil ? new Date(vacationUntil).getTime() : 0,
          ...(linkedSubjects.length > 0 ? { linkedSubjects } : {}),
          ...(allowedStudentIds.length > 0 ? { allowedStudentIds } : {}),
          // classroomId fra rummet saa StaffHub kan slaa klasseskema op
          // classroomIds fra linkedSubjects (tvaershold = flere klasser)
          ...(rooms[roomId]?.classroomId ? { classroomId: rooms[roomId].classroomId } : {}),
          classroomIds: linkedSubjects.length > 0
              ? [...new Set(linkedSubjects.map((ls) => {
                  const cls = hallsData.find((cl: any) =>
                      (cl.subjects || []).some((s) => s.id === ls.subjectId) || cl.id === ls.hallId
                  );
                  return cls ? cls.id : '';
              }).filter(Boolean))]
              : rooms[roomId]?.classroomId ? [rooms[roomId].classroomId] : [],
        };
        await setDoc(doc(db, `room_schedules/${roomId}/rituals`, id), ritual); logSisterAction(currentUser, "campus", "TID", "GEMT_SKEMA", id, "");
      }
      resetForm();
    } catch (err) {
      console.error("Fejl ved smedning:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setEditingGroupId(null);
    setTitle('');
    setDesc('');
    setTime('');
    setEndTime('');
    setRepeat('none');
    setIsVisible(true);
    setHiddenUntil('');
    setAccessLevel('all');
    setAssignedGuardianId('');
    setAssignedGuardianName('');
    setSendNotification(false);
    setCopyToRoomId('');
    setAllowedAgeGroups([]);
    if (initialRoomId) setTargetRoomIds([initialRoomId]);
    setLinkedSubjects([]);
    setAllowedStudentIds([]);
  };

  const handleEdit = (ritual: ScheduleRitual) => {
    setEditingId(ritual.id);
    setEditingGroupId(ritual.groupId || null);
    setDay(ritual.day);
    setTime(ritual.time);
    setEndTime(ritual.endTime || '');
    setRepeat(ritual.repeat || 'none');
    setRepeatInterval((ritual as any).repeatInterval || 1);
    setSeriesEnd((ritual as any).seriesEnd || '');
    setVisibleInVacations(!!(ritual as any).visibleInVacations);
    setEditingOriginal(ritual);
    setTitle(ritual.title);
    setDesc(ritual.description);
    setType(ritual.type);
    setIsVisible(ritual.isVisible !== false);
    setAccessLevel(ritual.accessLevel || 'all');
    setHiddenUntil(ritual.hiddenUntil ? new Date(ritual.hiddenUntil).toISOString().split('T')[0] : '');
    setTargetRoomId((ritual as any).targetRoomId || '');
    setVacationFrom((ritual as any).vacationFrom ? new Date((ritual as any).vacationFrom).toISOString().split('T')[0] : '');
    setVacationUntil((ritual as any).vacationUntil ? new Date((ritual as any).vacationUntil).toISOString().split('T')[0] : '');
    setAssignedGuardianId((ritual as any).assignedGuardianId || '');
    setAssignedGuardianName((ritual as any).assignedGuardianName || '');
    setTargetRoomIds([selectedRoomId]);
    setLinkedSubjects((ritual).linkedSubjects || []);
    setAllowedStudentIds((ritual as any).allowedStudentIds || []);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (ritual: ScheduleRitual) => {
    if (!canDelete) return;
    const msg = ritual.groupId 
      ? "Dette ritual findes måske i flere døre. Vil du slette det KUN i denne dør?" 
      : "Vil du slette dette ritual?";

    if (confirm(msg)) {
      await deleteDoc(doc(db, `room_schedules/${selectedRoomId}/rituals`, ritual.id)); logSisterAction(currentUser, "campus", "KONTROL", "SLETTET_SKEMA", ritual.id, "");
      if (editingId === ritual.id) resetForm();
    }
  };

  const toggleVisibility = async (ritual: ScheduleRitual) => {
    const nextValue = !(ritual.isVisible !== false);
    await setDoc(doc(db, `room_schedules/${selectedRoomId}/rituals`, ritual.id), { isVisible: nextValue }, { merge: true });
  };

  const handleToggleRoom = (id: string) => {
    setTargetRoomIds(prev => prev.includes(id) ? prev.filter(rid => rid !== id) : [...prev, id]);
  };

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-black/98 backdrop-blur-3xl animate-fade-in overflow-y-auto">
      <div className="bg-[#120a0a] border-2 border-orange-600/30 w-full max-w-6xl my-auto rounded-[3rem] overflow-hidden flex flex-col shadow-[0_0_120px_rgba(234,88,12,0.15)] relative min-h-[90vh]">
        
        <div className="p-6 md:p-8 border-b border-orange-600/10 flex justify-between items-center bg-gradient-to-r from-orange-950/40 to-black shrink-0">
          <div className="flex items-center gap-6">
            <span className="text-4xl md:text-5xl drop-shadow-[0_0_15px_rgba(234,88,12,0.5)]">⚒️</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-cinzel text-orange-500 tracking-widest uppercase">Smedjen</h2>
              <p className="text-[10px] font-black uppercase text-orange-400/60 tracking-[0.3em]">Formgivning af Akademiets Tid</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-5xl transition-colors leading-none">&times;</button>
        </div>

        <div className="flex-1 flex overflow-hidden flex-col md:flex-row">
          {/* SIDEBAR */}
          <div className="w-full md:w-80 border-b md:border-b-0 md:border-r border-orange-600/10 bg-black/40 flex flex-col p-6 space-y-4 overflow-y-auto no-scrollbar shrink-0">
             <button 
                onClick={() => { setIsGlobalSettings(true); setSelectedRoomId(''); }}
                className={`w-full text-left p-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${isGlobalSettings ? 'bg-indigo-600 text-white border-indigo-400 shadow-lg' : 'bg-white/5 border-transparent text-indigo-200 hover:bg-white/10'}`}
             >
                🌐 Akademi-indstillinger
             </button>

             <div className="pt-4 border-t border-orange-600/10">
             <h4 className="text-xs font-black text-orange-400 uppercase tracking-widest px-2 mb-1">Arkiv-valg</h4>
             <p className="text-[10px] text-white/60 px-2 mb-3">Vælg et rum for at se og redigere dets lektioner.</p>
               <div className="space-y-2">
                 {Object.values(rooms).filter(r => r.id !== 'HALLWAY' && r.id !== 'config_global').map(room => (
                   <button 
                     key={room.id} 
                     onClick={() => { setSelectedRoomId(room.id); setIsGlobalSettings(false); resetForm(); }}
                     className={`w-full text-left p-3 rounded-xl text-[11px] font-bold transition-all border ${selectedRoomId === room.id ? 'bg-orange-600 text-white border-orange-400 shadow-lg' : 'bg-white/5 border-transparent text-orange-200 hover:bg-white/10'}`}
                   >
                     🔮 {s(room.name)}
                   </button>
                 ))}
               </div>
             </div>
          </div>

          {/* EDITOR */}
          <div className="flex-1 flex flex-col overflow-y-auto no-scrollbar bg-black/20">
            {isGlobalSettings ? (
              <div className="max-w-2xl mx-auto py-20 px-6 space-y-12 animate-fade-in">
                 <div className="bg-indigo-950/40 p-10 rounded-[2.5rem] border border-indigo-500/30 space-y-8 shadow-2xl">
                    <h3 className="text-2xl font-cinzel text-indigo-400 uppercase tracking-widest">Akademiets Portaler</h3>
                    <div className="space-y-6">
                       <div className="space-y-3">
                          <label className="text-[10px] font-black text-indigo-300 uppercase tracking-widest ml-1">Domæne Link (URL)</label>
                          <input type="url" placeholder="https://norewyn.dk" value={academyUrl} onChange={e => setAcademyUrl(e.target.value)} className="w-full bg-indigo-900/40 border-2 border-indigo-500/30 p-5 rounded-2xl text-white outline-none focus:border-indigo-400 font-mono" />
                       </div>
                       <button onClick={async () => {
                         setIsSaving(true);
                         await setDoc(doc(db, "rooms", "config_global"), { mainAcademyUrl: academyUrl }, { merge: true });
                         setIsSaving(false);
                       }} disabled={isSaving} className="bg-indigo-600 hover:bg-indigo-500 text-white font-black uppercase tracking-widest py-5 px-10 rounded-2xl transition-all border-b-4 border-indigo-800 shadow-xl w-full">Gem Indstillinger</button>
                    </div>
                 </div>
              </div>
            ) : selectedRoomId ? (
              <div className="p-6 md:p-10 space-y-12 pb-32">
                 <div className="bg-orange-950/5 border border-orange-600/10 p-8 rounded-[2.5rem] relative">
                    <h3 className="text-xl font-cinzel text-orange-500 uppercase tracking-widest mb-8 flex items-center gap-3">
                      {editingId ? '⚡ Ret Ritual' : '⚒️ Smed Nyt Ritual'}
                      {editingId && <button onClick={resetForm} className="text-[9px] bg-white/5 px-3 py-1 rounded-full text-white/40 hover:text-white uppercase tracking-widest ml-auto">Annuller</button>}
                    </h3>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                       <div className="xl:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                             <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Ugedag</label>
                             <select value={day} onChange={e => setDay(e.target.value as any)} className="w-full bg-black/60 border border-orange-600/20 p-4 rounded-2xl text-white outline-none focus:border-orange-500/50">
                               {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                             </select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                               <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Start-tid</label>
                               <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full bg-black/60 border border-orange-600/20 px-3 py-2 rounded-2xl text-white outline-none" />
                            </div>
                            <div className="space-y-2">
                               <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Slut-tid</label>
                               <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full bg-black/60 border border-orange-600/20 px-3 py-2 rounded-2xl text-white outline-none" />
                            </div>
                          </div>
                          <div className="space-y-2 md:col-span-2">
                             <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Ritualets Titel</label>
                             <input type="text" placeholder="Navn på lektion..." value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-black/60 border border-orange-600/20 p-4 rounded-2xl text-white outline-none focus:border-orange-500/50" />
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Type</label>
                             <select value={type} onChange={e => setType(e.target.value as any)} className="w-full bg-black/60 border border-orange-600/20 p-4 rounded-2xl text-white outline-none">
                               {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                             </select>
                          </div>
                          <div className="space-y-2">
                             <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Gentagelse</label>
                             <select value={repeat} onChange={e => setRepeat(e.target.value as any)} className="w-full bg-black/60 border border-orange-600/20 p-4 rounded-2xl text-white outline-none">
                               {REPEAT_OPTIONS.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                             </select>
                             {repeat !== 'none' && (
                               <div className="flex gap-2">
                                 <input type="number" min={1} max={8} value={repeatInterval} onChange={e => setRepeatInterval(Number(e.target.value) || 1)} title="Hver X" className="w-20 bg-black/60 border border-orange-600/20 p-3 rounded-2xl text-white outline-none text-sm" />
                                 <input type="date" value={seriesEnd} onChange={e => setSeriesEnd(e.target.value)} title="Serie slutter" className="flex-1 bg-black/60 border border-orange-600/20 p-3 rounded-2xl text-white outline-none text-sm" />
                               </div>
                             )}
                             <label className="flex items-center gap-2 text-[10px] text-white/60 cursor-pointer">
                               <input type="checkbox" checked={visibleInVacations} onChange={e => setVisibleInVacations(e.target.checked)} /> {'Vis i skole\u00e5rets ferieperioder'}
                             </label>
                          </div>

                          <div className="space-y-2">
                             <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Ansvarlig Vogter (valgfri)</label>
                             <select value={assignedGuardianId} onChange={e => { const g = guardians.find(g => g.id === e.target.value); setAssignedGuardianId(e.target.value); setAssignedGuardianName(g ? (g.username || g.wizardName || '') : ''); }} className="w-full bg-black/60 border border-orange-600/20 p-3 rounded-2xl text-white outline-none text-sm">
                               <option value="">— Ingen specifik vogter —</option>
                               {guardians.map(g => <option key={g.id} value={g.id}>{g.username || g.wizardName || g.name}</option>)}
                             </select>
                          </div>
          {/* NYT: Vælger til Adgangsniveau */}
                          <div className="space-y-2 md:col-span-2">
                             <label className="text-xs font-black text-orange-300 uppercase tracking-widest ml-1">Adgang (Pakke-krav)</label>
                             <div className="flex gap-4">
                                {ACCESS_LEVELS.map(level => (
                                   <button 
                                      key={level.id}
                                      onClick={() => setAccessLevel(level.id as any)}
                                      className={`flex-1 py-4 rounded-xl text-[10px] font-black uppercase border-2 transition-all ${accessLevel === level.id ? 'bg-orange-600 border-orange-400 text-white' : 'bg-black border-white/5 text-white/50'}`}
                                   >
                                      {level.label}
                                   </button>
                                ))}
                             </div>
                          </div>

                          {/* TARGET ROOM */}
                          <div className="space-y-2 md:col-span-2">
                             <label className="text-xs font-black text-yellow-300 uppercase tracking-widest ml-1">🚪 Foregår i andet rum (valgfrit)</label>
                             <select value={targetRoomId} onChange={e => setTargetRoomId(e.target.value)}
                               className="w-full bg-black/60 border border-yellow-500/20 p-3 rounded-xl text-white outline-none text-xs focus:border-yellow-500/50">
                               <option value="">— Dette rum (standard) —</option>
                               {Object.values(rooms).filter(r => r.id !== 'config_global').map(r => (
                                 <option key={r.id} value={r.id}>{s(r.name)}</option>
                               ))}
                             </select>
                             {targetRoomId && <p className="text-[10px] text-yellow-300/80 italic ml-1">Elever vil se "Gå til rum"-knap i skemaet</p>}
                          </div>
                          <div className="space-y-4 bg-indigo-950/30 p-6 rounded-[2rem] border border-indigo-500/20 md:col-span-2">
                            <h5 className="text-[10px] font-black text-indigo-300 uppercase tracking-widest border-b border-indigo-500/20 pb-2">📅 Ferieperiode — lektion springes over</h5>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <label className="text-[10px] font-black text-white uppercase tracking-widest ml-1">Fra dato</label>
                                <input type="date" value={vacationFrom} onChange={e => setVacationFrom(e.target.value)} className="w-full bg-black/60 border border-indigo-500/30 p-3 rounded-xl text-white outline-none text-sm" />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] font-black text-white uppercase tracking-widest ml-1">Til dato</label>
                                <input type="date" value={vacationUntil} onChange={e => setVacationUntil(e.target.value)} className="w-full bg-black/60 border border-indigo-500/30 p-3 rounded-xl text-white outline-none text-sm" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-indigo-500/10">
                              <div className="space-y-2">
                                <label className="text-[10px] font-black text-white uppercase tracking-widest ml-1">Skjul lektion indtil dato</label>
                                <input type="date" value={hiddenUntil} onChange={e => setHiddenUntil(e.target.value)} className="w-full bg-black/60 border border-indigo-500/30 p-3 rounded-xl text-white outline-none text-sm" />
                                <p className="text-[10px] text-indigo-200/80 italic ml-1">Lektionen vises ikke i skemaet før denne dato</p>
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] font-black text-white uppercase tracking-widest ml-1">Lektionen er synlig</label>
                                <div className="flex items-center gap-3 mt-2">
                                  <button onClick={() => setIsVisible(!isVisible)} className={`w-12 h-6 rounded-full relative transition-all ${isVisible ? 'bg-green-600' : 'bg-red-600'}`}>
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isVisible ? 'left-7' : 'left-1'}`} />
                                  </button>
                                  <span className={`text-sm font-black uppercase ${isVisible ? 'text-green-400' : 'text-red-400'}`}>{isVisible ? 'Synlig' : 'Skjult'}</span>
                                </div>
                                <p className="text-[10px] text-indigo-200/80 italic ml-1">Skjul lektionen manuelt fra alle skemaer</p>
                              </div>
                            </div>
                          </div>

                       {/* SYNLIGHED PER ALDERSGRUPPE */}
                       <div className="space-y-3 md:col-span-2 bg-white/5 p-5 rounded-2xl border border-white/5">
                            <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Aldersgrupper (tom = alle)</label>
                            <div className="flex flex-wrap gap-2">
                              {['0.kl','1.kl','2.kl','3.kl','4.kl','5.kl','6.kl','7.kl','8.kl','9.kl','10.kl','UU','Forældre'].map(ag => {
                                const selected = allowedAgeGroups.includes(ag);
                                return (
                                  <button key={ag} onClick={() => setAllowedAgeGroups(prev => selected ? prev.filter(x => x !== ag) : [...prev, ag])} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase border transition-all ${selected ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/30 border-white/10 text-white/40'}`}>
                                    {ag}
                                  </button>
                                );
              })}
                            </div>
                          </div>
                          {/* SPECIFIKKE ELEVER */}
                          <div className="space-y-3 md:col-span-2 bg-white/5 p-5 rounded-2xl border border-white/5">
                            <div className="flex items-center justify-between">
                              <label className="text-[9px] font-black text-purple-400 uppercase tracking-widest">
                                👤 Specifikke elever (tom = alle med adgang)
                                {allowedStudentIds.length > 0 && <span className="ml-2 text-yellow-400">({allowedStudentIds.length} valgt)</span>}
                              </label>
                              {allowedStudentIds.length > 0 && (
                                <button onClick={() => setAllowedStudentIds([])} className="text-[9px] text-white/30 hover:text-red-400 transition-all">Ryd</button>
                              )}
                            </div>
                            <p className="text-[9px] text-white/25">Tom = ingen elev-begrænsning. Kombineres med aldersgrupper via OR-logik.</p>
                            {classroomStudents.length === 0 ? (
                              <p className="text-[9px] text-white/20 italic">Ingen elever fundet</p>
                            ) : (
                              <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                                {classroomStudents.map((st: any) => {
                                  const isSelected = allowedStudentIds.includes(st.id);
                                  return (
                                    <button key={st.id}
                                      onClick={() => setAllowedStudentIds(prev => isSelected ? prev.filter(id => id !== st.id) : [...prev, st.id])}
                                      className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase border transition-all ${isSelected ? 'bg-purple-700 border-purple-400 text-white' : 'bg-black/30 border-white/10 text-white/40 hover:text-white/70'}`}>
                                      {st.username || st.wizardName || st.name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {/* NOTIFIKATION + KOPIER */}
                          <div className="space-y-3 md:col-span-2 bg-white/5 p-5 rounded-2xl border border-white/5">
                            <div className="flex items-center justify-between">
                              <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Send notifikation ved oprettelse</label>
                              <button onClick={() => setSendNotification(!sendNotification)} className={`w-10 h-5 rounded-full relative transition-all ${sendNotification ? 'bg-green-600' : 'bg-gray-700'}`}>
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${sendNotification ? 'left-5' : 'left-0.5'}`} />
                              </button>
                            </div>
                            <div className="space-y-2">
                              <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Kopier ritual til andet rum</label>
                              <div className="flex gap-2">
                                <select value={copyToRoomId} onChange={e => setCopyToRoomId(e.target.value)} className="flex-1 bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none">
                                  <option value="">— Vælg rum —</option>
                                  {Object.values(rooms).filter(r => r.id !== 'config_global' && r.id !== selectedRoomId).map(r => (
                                    <option key={r.id} value={r.id}>{s(r.name)}</option>
                                  ))}
                                </select>
                                <button onClick={async () => {
                                  if (!copyToRoomId || !title || !time) return;
                                  const id = `${Date.now()}_${copyToRoomId}`;
                                  logSisterAction(currentUser, "campus", "TID", "KOPIERET_SKEMA", id, ""); await setDoc(doc(db, `room_schedules/${copyToRoomId}/rituals`, id), {
                                    id, day, time, endTime, repeat, repeatInterval: repeatInterval || 1, ...(seriesEnd ? { seriesEnd } : {}), visibleInVacations, seriesStart: new Date().toLocaleDateString('sv-SE'), title, description: desc, type, isVisible, accessLevel,
                                    ...(assignedGuardianId ? { assignedGuardianId } : {}),
                                    ...(assignedGuardianName ? { assignedGuardianName } : {}),
                                    ...(linkedSubjects.length > 0 ? { linkedSubjects } : {}),
                                    ...(allowedStudentIds.length > 0 ? { allowedStudentIds } : {}),
                                    ...(allowedAgeGroups.length > 0 ? { allowedAgeGroups } : {}),
                                    groupId: editingGroupId || editingId || Date.now().toString(),
                                    sourceRoomId: copyToRoomId, /* pariCopy */
                                    ...(rooms[copyToRoomId]?.classroomId ? { classroomId: rooms[copyToRoomId].classroomId, classroomIds: [rooms[copyToRoomId].classroomId] } : {}),
                                  });
                                  setCopyToRoomId('');
                                  alert('Kopieret!');
                                }} disabled={!copyToRoomId} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase disabled:opacity-30">Kopier</button>
                              </div>
                            </div>
                          </div>
                       </div>

                       <div className="xl:col-span-4 bg-black/40 p-6 rounded-[2rem] border border-white/5 flex flex-col min-h-[300px]">
                       <h4 className="text-xs font-black text-orange-300 uppercase tracking-widest mb-1">Mål-døre</h4>
                       <p className="text-[10px] text-white/60 mb-3">Vælg hvilke rum lektionen oprettes i — vælg flere for at smede i flere rum på én gang.</p>
                          <div className="flex-1 space-y-2 overflow-y-auto pr-2 custom-scrollbar">
                             {Object.values(rooms).filter(r => r.id !== 'HALLWAY' && r.id !== 'config_global').map(room => (
                               <button key={room.id} onClick={() => handleToggleRoom(room.id)} className={`w-full flex items-center justify-between p-3 rounded-xl border text-[10px] font-bold transition-all ${targetRoomIds.includes(room.id) ? 'bg-orange-600/20 border-orange-500 text-orange-300' : 'bg-white/5 border-transparent text-white/30'}`}>
                                 <span>{s(room.name)}</span>
                                 <span>{targetRoomIds.includes(room.id) ? '✅' : '○'}</span>
                               </button>
                             ))}
                          </div>
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <h5 className="text-[9px] font-black text-blue-300 uppercase tracking-widest mb-1">🚪 AcademyHalls fagdøre (valgfrit)</h5>
                            <p className="text-[9px] text-white/40 mb-2">Tænder Live-badge automatisk i fagdøren under timen. Lad stå tomt hvis ikke relevant.</p>
                            {hallsData.map(hall => (
                              <div key={hall.id} className="mb-3">
                                <p className="text-[8px] text-orange-400/60 font-black uppercase mb-1 pl-1">{hall.name}</p>
                                <div className="space-y-1 pl-2">
                                  {(hall.subjects || []).filter(subj => (subj.isActive !== false)).map((subj) => {
                                    const subjName = typeof subj.name === 'string' ? subj.name : subj.name?.da || subj.name?.en || 'Fagdør';
                                    const isLinked = linkedSubjects.some(ls => ls.hallId === hall.id && ls.subjectId === subj.id);
                                    return (
                                      <button key={subj.id}
                                        onClick={() => setLinkedSubjects(prev => isLinked
                                          ? prev.filter(ls => !(ls.hallId === hall.id && ls.subjectId === subj.id))
                                          : [...prev, { hallId: hall.id, subjectId: subj.id }])}
                                        className={`w-full flex items-center justify-between p-2 rounded-lg border text-[9px] font-bold transition-all ${isLinked ? 'bg-blue-600/20 border-blue-500 text-blue-300' : 'bg-white/5 border-transparent text-white/30 hover:bg-white/10'}`}>
                                        <span>{subjName}</span>
                                        <span>{isLinked ? '🔵' : '○'}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                          <button onClick={handleSaveRitual} disabled={isSaving || targetRoomIds.length === 0} className="w-full mt-6 py-5 bg-orange-600 hover:bg-orange-500 rounded-2xl font-black uppercase text-xs shadow-xl active:scale-95 transition-all disabled:opacity-50">
                             {isSaving ? 'Smeder...' : '⚒️ Smed i Skemaet'}
                          </button>
                       </div>
                    </div>
                 </div>

                 <div className="space-y-6">
                    <h3 className="text-2xl font-cinzel text-white uppercase tracking-widest px-4">Arkivet for {s(rooms[selectedRoomId]?.name)}</h3>
                    {rituals.filter(fgIsEnded).length > 0 && (
                      <button onClick={() => setShowArchive(v => v === false)} className={'mx-4 px-4 py-2 rounded-xl text-[10px] font-black uppercase border transition-all ' + (showArchive ? 'bg-gray-600/40 border-gray-400/60 text-white' : 'bg-black/40 border-white/15 text-gray-400 hover:text-white hover:border-white/40')}>
                        {'\uD83D\uDCE6'} {showArchive ? 'Skjul afsluttede serier' : 'Afsluttede serier \u2014 ' + rituals.filter(fgIsEnded).length}
                      </button>
                    )}
                    <div className="grid grid-cols-1 gap-4">
                       {DAYS.map(d => {
                         const dayRituals = rituals.filter(r => r.day === d && (showArchive || !fgIsEnded(r)));
                         if (dayRituals.length === 0) return null;
                         return (
                           <div key={d} className="bg-white/5 p-6 rounded-[2rem] border border-white/5">
                              <h4 className="text-orange-500 font-black text-[11px] uppercase tracking-widest mb-4 border-l-2 border-orange-500 pl-3">{d}</h4>
                              <div className="space-y-2">
                                 {dayRituals.map(r => (
                                   <div key={r.id} className="bg-black/40 border border-white/5 p-4 rounded-xl flex justify-between items-center group">
                                      <div className="flex items-center gap-6">
                                         <span className="font-mono text-orange-400 text-sm font-bold">{r.time}</span>
                                         <div className="flex flex-col">
                                            <span className={`font-bold transition-opacity ${r.isVisible === false || (r.hiddenUntil && Date.now() < r.hiddenUntil) ? 'opacity-30' : 'opacity-100'}`}>
                                               {r.title}
                                            </span>
                                            {/* NYT: Visuel markør for Plus-indhold i listen */}
                                            {r.accessLevel === 'plus' && (
                                               <span className="text-[7px] text-amber-500 font-black uppercase tracking-tighter">💎 Plus-Eksklusiv</span>
                                            )}
                                         </div>
                                         {r.groupId && <span className="text-[8px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded uppercase font-black">🔗 Fælles</span>}
                                         {(r as any).seriesEnd && (r as any).seriesEnd < fgToday && <span className="text-[8px] bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded uppercase font-black">Sluttet {fgDate((r as any).seriesEnd)}</span>}
                                         {(r as any).seriesStart && (r as any).seriesStart > fgToday && <span className="text-[8px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded uppercase font-black">Fra {fgDate((r as any).seriesStart)}</span>}
                                      </div>
                                      <div className="flex gap-4 items-center">
                                         <button onClick={() => toggleVisibility(r)} className="text-xl" title={r.isVisible !== false ? 'Synlig' : 'Skjult'}>
                                            {r.isVisible !== false ? '👁️' : '👁️‍G'}
                                         </button>
                                         <button onClick={() => handleEdit(r)} className="text-[9px] font-black uppercase text-indigo-400 hover:text-white transition-colors">Ret</button>
                                         {canDelete && <button onClick={() => handleDelete(r)} className="text-[9px] font-black uppercase text-red-500 hover:text-red-400 transition-colors">Slet</button>}
                                      </div>
                                   </div>
                                 ))}
                              </div>
                           </div>
                         );
                       })}
                    </div>
                 </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center opacity-10">
                 <span className="text-9xl mb-8">🔥</span>
                 <p className="font-cinzel text-2xl uppercase tracking-[0.5em]">Vælg et arkiv</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheForge;